/**
 * CharacterDedupeDialog
 *
 * Scans the project's character roster for duplicate/alias entries and lets
 * the author merge them one pair at a time.
 *
 * Flow:
 *   1. Parent opens dialog → component auto-fires the scan API.
 *   2. Each detected pair is shown side-by-side. Author clicks the card of
 *      the character they want to KEEP (the other is absorbed then deleted).
 *   3. "Merge" confirms. "Not duplicates" skips the pair without touching data.
 *   4. Once all pairs are resolved the dialog closes and the caller receives
 *      an updated characters array via `onMerged`.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, GitMerge, SkipForward, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Confidence badge styles
const CONFIDENCE_STYLE = {
  high:   'bg-destructive/15 text-destructive border-destructive/30',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
};

export default function CharacterDedupeDialog({ open, onClose, projectId, onMerged }) {
  const [phase, setPhase]   = useState('idle');   // idle | scanning | reviewing | done
  const [pairs, setPairs]   = useState([]);
  const [cursor, setCursor] = useState(0);         // index into pairs currently shown
  const [keepId, setKeepId] = useState(null);      // id of the card the author clicked
  const [merging, setMerging] = useState(false);
  const [resolved, setResolved] = useState([]);    // ids of characters that were deleted

  // Reset and scan every time the dialog opens
  useEffect(() => {
    if (!open || !projectId) return;
    setPhase('scanning');
    setPairs([]);
    setCursor(0);
    setKeepId(null);
    setResolved([]);

    axios.post(`${API}/projects/${projectId}/characters/find-duplicates`, {}, { withCredentials: true, timeout: 30000 })
      .then((r) => {
        const found = r.data?.pairs || [];
        if (found.length === 0) {
          setPhase('done');
        } else {
          setPairs(found);
          setKeepId(found[0].char_a.id);   // pre-select first card
          setPhase('reviewing');
        }
      })
      .catch(() => {
        setPhase('error');
      });
  }, [open, projectId]);

  // When cursor advances, pre-select char_a of the new pair
  useEffect(() => {
    if (phase === 'reviewing' && pairs[cursor]) {
      setKeepId(pairs[cursor].char_a.id);
    }
  }, [cursor, phase, pairs]);

  const currentPair = pairs[cursor] || null;
  const deleteId = currentPair
    ? (keepId === currentPair.char_a.id ? currentPair.char_b.id : currentPair.char_a.id)
    : null;

  const advance = useCallback(() => {
    if (cursor + 1 < pairs.length) {
      setCursor((c) => c + 1);
    } else {
      setPhase('done');
    }
  }, [cursor, pairs.length]);

  const handleMerge = useCallback(async () => {
    if (!keepId || !deleteId) return;
    setMerging(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/characters/merge`,
        { keep_id: keepId, delete_id: deleteId },
        { withCredentials: true, timeout: 20000 },
      );
      const kept = r.data?.kept;
      setResolved((prev) => [...prev, { deleteId, kept }]);
      toast.success(`Merged — kept "${kept?.name || keepId}".`);
      advance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed — please try again.');
    } finally {
      setMerging(false);
    }
  }, [keepId, deleteId, projectId, advance]);

  const handleSkip = useCallback(() => {
    advance();
  }, [advance]);

  // Tell the parent about all merges once the dialog is dismissed
  const handleClose = useCallback(() => {
    if (resolved.length > 0) {
      onMerged?.(resolved);
    }
    onClose?.();
  }, [resolved, onMerged, onClose]);

  // ── Render helpers ────────────────────────────────────────────────────────

  function CharCard({ char, selected, onClick }) {
    return (
      <button
        type="button"
        data-testid={`dedupe-card-${char.id}`}
        onClick={onClick}
        className={cn(
          'flex w-full flex-col gap-1.5 rounded-lg border-2 p-3 text-left transition-all',
          selected
            ? 'border-primary bg-primary/8 shadow-sm'
            : 'border-border bg-card hover:border-primary/40 hover:bg-primary/4',
        )}
      >
        <div className="flex items-center gap-2">
          <span className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
          )}>
            {(char.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold leading-tight">{char.name}</p>
            <p className="text-[11px] text-muted-foreground">{char.role || 'no role'}</p>
          </div>
        </div>
        {selected && (
          <div className="flex items-center gap-1 text-[10.5px] font-semibold text-primary">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Keep this one
          </div>
        )}
        {!selected && (
          <div className="text-[10.5px] text-muted-foreground">
            Click to keep this one
          </div>
        )}
      </button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="character-dedupe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <GitMerge className="h-4 w-4 text-primary" />
            Find &amp; Merge Duplicate Characters
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {phase === 'scanning' && 'Scanning the roster for duplicate entries…'}
            {phase === 'reviewing' && `Pair ${cursor + 1} of ${pairs.length} — click the card you want to keep, then confirm.`}
            {phase === 'done' && resolved.length === 0 && 'No duplicates found — your roster looks clean.'}
            {phase === 'done' && resolved.length > 0 && `Done. ${resolved.length} duplicate${resolved.length === 1 ? '' : 's'} merged.`}
            {phase === 'error' && 'The scan failed. Check your connection and try again.'}
          </DialogDescription>
        </DialogHeader>

        {/* ── Scanning spinner ────────────────────────────────────────────── */}
        {phase === 'scanning' && (
          <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-[12px]">Checking names for near-matches…</p>
          </div>
        )}

        {/* ── Reviewing a pair ────────────────────────────────────────────── */}
        {phase === 'reviewing' && currentPair && (
          <div className="space-y-3">
            {/* Reason badge */}
            <div className={cn(
              'flex items-start gap-2 rounded-md border px-3 py-2 text-[11.5px]',
              CONFIDENCE_STYLE[currentPair.confidence] || CONFIDENCE_STYLE.medium,
            )}>
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{currentPair.reason}</span>
            </div>

            {/* Two character cards */}
            <div className="grid grid-cols-2 gap-2">
              <CharCard
                char={currentPair.char_a}
                selected={keepId === currentPair.char_a.id}
                onClick={() => setKeepId(currentPair.char_a.id)}
              />
              <CharCard
                char={currentPair.char_b}
                selected={keepId === currentPair.char_b.id}
                onClick={() => setKeepId(currentPair.char_b.id)}
              />
            </div>

            {/* Merge note */}
            <p className="text-[11px] text-muted-foreground">
              The card you select is <strong>kept</strong>. The other card's arc data and
              any Bible fields missing from the kept card are copied over, then the
              duplicate is permanently deleted.
            </p>
          </div>
        )}

        {/* ── Done ────────────────────────────────────────────────────────── */}
        {phase === 'done' && (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            {resolved.length === 0 ? (
              <>
                <Users className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-[13px] font-semibold">Roster is clean</p>
                <p className="text-[11.5px] text-muted-foreground">No duplicate entries were detected.</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-10 w-10 text-success" />
                <p className="text-[13px] font-semibold">All done</p>
                <div className="mt-1 space-y-1">
                  {resolved.map((r) => (
                    <Badge key={r.deleteId} variant="outline" className="text-[10.5px]">
                      Kept "{r.kept?.name}"
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Error ───────────────────────────────────────────────────────── */}
        {phase === 'error' && (
          <div className="flex flex-col items-center gap-2 py-6 text-center text-destructive">
            <AlertCircle className="h-8 w-8" />
            <p className="text-[12.5px] font-semibold">Scan failed</p>
            <p className="text-[11.5px] text-muted-foreground">The server could not complete the scan. Please try again.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'reviewing' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                disabled={merging}
                data-testid="dedupe-skip-btn"
                className="gap-1.5"
              >
                <SkipForward className="h-3.5 w-3.5" />
                Not duplicates
              </Button>
              <Button
                size="sm"
                onClick={handleMerge}
                disabled={merging || !keepId}
                data-testid="dedupe-merge-btn"
                className="gap-1.5"
              >
                {merging
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <GitMerge className="h-3.5 w-3.5" />}
                {merging ? 'Merging…' : 'Merge'}
              </Button>
            </>
          )}
          {(phase === 'done' || phase === 'error') && (
            <Button size="sm" onClick={handleClose} data-testid="dedupe-close-btn">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
